// GET /api/v0/tenants/:tenantId/projects/:projectId/document-types のモックハンドラ (Issue #447)。
// enabledOnly (default true) / category クエリでフィルタしたレスポンスを返す。
// openapi 契約 (400 バリデーションエラー / 404 プロジェクト未存在) も再現する。

import { HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';

import { MOCK_TENANT_DOCUMENT_TYPES } from '../mock-data/document-types.mock';
import { findProjectById } from '../mock-data/projects.mock';
import { createMockResponse } from '../mock.interceptor';

import type {
  DocumentTypeApiResponse,
  DocumentTypeListApiResponse,
} from '../../features/evidence-files/document-types.models';
import type { DocumentTypeCategory } from '../../features/evidence-files/required-documents.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * ProblemDetail (RFC 9457) 相当の body。api-server の 400/404 応答と揃える。
 */
type ProblemBody = {
  type?: string;
  title: string;
  status: number;
  detail?: string;
};

/**
 * /projects/:projectId/document-types へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス
 */
export function handleDocumentTypesRequest(
  method: string,
  path: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<unknown>> | null {
  const match = /^\/projects\/([^/]+)\/document-types$/.exec(path);
  if (method === 'GET' && match) {
    const projectId = match[1];
    return handleGetDocumentTypes(projectId, searchParams);
  }
  return null;
}

/**
 * category クエリ値のバリデーション用 type guard。
 * null は「未指定＝絞り込みなし」を表す有効値として扱う。
 */
function isValidCategoryOrNull(
  value: string | null,
): value is DocumentTypeCategory | null {
  return (
    value === null ||
    value === 'common' ||
    value === 'employment' ||
    value === 'journal_entry'
  );
}

/**
 * enabledOnly クエリ値のバリデーション用 type guard。
 * null (未指定＝server default) / "true" / "false" のみ有効。
 */
function isValidEnabledOnlyOrNull(
  value: string | null,
): value is 'true' | 'false' | null {
  return value === null || value === 'true' || value === 'false';
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/document-types
 * - enabledOnly (default true): enable=false のエントリを除外
 * - category: 指定カテゴリのみに絞り込む
 * - 400: category / enabledOnly の値が enum / boolean 外の場合
 * - 404: projectId が存在しない場合 (api-server の existence oracle と同挙動)
 */
function handleGetDocumentTypes(
  projectId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<DocumentTypeListApiResponse>> {
  // 404: 存在しない projectId は api-server 側で existence oracle により 404 統一
  if (findProjectById(projectId) === undefined) {
    return createProblemResponse(
      404,
      'Not Found',
      `projectId '${projectId}' not found`,
    );
  }

  // 400: enabledOnly は "true" / "false" のみ許容 (未指定なら server default=true)
  const enabledOnlyRaw = searchParams.get('enabledOnly');
  if (!isValidEnabledOnlyOrNull(enabledOnlyRaw)) {
    return createProblemResponse(
      400,
      'Bad Request',
      `enabledOnly must be 'true' or 'false', but got '${enabledOnlyRaw}'`,
    );
  }
  const enabledOnly = enabledOnlyRaw !== 'false';

  // 400: category は enum ('common' | 'employment' | 'journal_entry') のみ許容
  const categoryRaw = searchParams.get('category');
  if (!isValidCategoryOrNull(categoryRaw)) {
    return createProblemResponse(
      400,
      'Bad Request',
      `category must be one of 'common' | 'employment' | 'journal_entry', but got '${categoryRaw}'`,
    );
  }

  const items: DocumentTypeApiResponse[] = MOCK_TENANT_DOCUMENT_TYPES.filter(
    (d) => {
      if (enabledOnly && !d.enable) return false;
      if (categoryRaw !== null && d.category !== categoryRaw) return false;
      return true;
    },
  );

  return createMockResponse<DocumentTypeListApiResponse>({
    items,
    nextCursor: null,
  });
}

/**
 * ProblemDetail (application/problem+json) 形式のエラー応答を Observable として返す。
 * Angular HttpClient は throwError で流された HttpErrorResponse を error channel に伝播するため、
 * `createMockResponse` (200 系 HttpResponse) ではなく HttpErrorResponse + throwError を使う。
 */
function createProblemResponse<T>(
  status: number,
  title: string,
  detail: string,
): Observable<HttpResponse<T>> {
  const body: ProblemBody = {
    type: 'about:blank',
    title,
    status,
    detail,
  };
  return throwError(
    () =>
      new HttpErrorResponse({
        status,
        statusText: title,
        error: body,
        headers: undefined,
      }),
  );
}
